import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userApi } from '../api/axiosInstance';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-hot-toast';
import { clearAuthStorage } from '../../utils/authStorage';
import {
  getUserProfile,
  getUserById as getUserByIdService,
  updateProfile,
  updatePassword as updatePasswordService,
  deleteUser as deleteUserService,
  verifyEmail,
  sendResetLink,
  resetPassword as resetPasswordService,
  addCandidateSignup,
  selfUpdatePassword as selfUpdatePasswordService,
} from '../../services/AdminServices/userManagementServices/userService';


interface User {
  user_id: number;
  name: string;
  email: string;
  contactNumber?: string;
  imageUrl?: string | null;
  isEmailVerified?: boolean;
  role: string;
  permission: string[];
  isActive?: boolean;
  isDefaultPass?: boolean;
  [key: string]: any;
}


interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: any;
  isAuthenticated: boolean;
  signupStatus: string | null;
  otpVerified: boolean;
  passwordResetStatus: string | null;
  passwordResetRequestStatus: string | null;
  activeRole: string | null;
  /** Deduplicated permission strings from JWT — UI-layer only */
  permissions: string[];
  /** primarysid from JWT — employee HR record ID (for EM/Payroll APIs) */
  employeeId: number | null;
  /** nameid from JWT — user account ID (for UM APIs) */
  userId: number | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  signupStatus: null,
  otpVerified: false,
  passwordResetStatus: null,
  passwordResetRequestStatus: null,
  activeRole: null,
  permissions: [],
  employeeId: null,
  userId: null,
};

const normalizeRoles = (role: any): { role: string; roles: string[] } => {
  if (!role) return { role: '', roles: [] };

  if (typeof role === 'string') {
    const roles = role.split(',').map(r => r.trim()).filter(Boolean);
    return { role: roles[0] || '', roles };
  }

  if (Array.isArray(role)) {
    const roles = role.map(String).map(r => r.trim()).filter(Boolean);
    return { role: roles[0] || '', roles };
  }

  return { role: String(role), roles: [String(role)] };
};


export const loginUser = createAsyncThunk<
  { token: string; sessionToken: string; userData: User },
  { email: string; password: string },
  { rejectValue: any }
>('auth/loginUser', async (credentials, { rejectWithValue }) => {
  try {
    const { data } = await userApi.post('Login/login', credentials);
    const decoded: any = jwtDecode(data.token);

    const { role, roles } = normalizeRoles(decoded.role);

    const userData: User = {
      user_id: Number(decoded.nameid),
      email: decoded.email,
      name: decoded.name || decoded.email.split('@')[0],
      role,
      roles,
      permission: Array.isArray(decoded.permission) ? decoded.permission : [decoded.permission].filter(Boolean),
      organizationId: decoded.organizationId || decoded.OrganizationId,
      isDefaultPass: false,
      employee_Id: null,
    };

    return { token: data.token, sessionToken: data.sessionToken, userData };
  } catch (err: any) {
    console.error('❌ Login error details:', err);
    const errorData = err.response?.data;
    const errorMessage = errorData?.details ||
      errorData?.error ||
      errorData?.message ||
      err.message ||
      'Authentication failed';

    console.error('❌ Extracted error message:', errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const fetchCurrentUser = createAsyncThunk<
  { token: string; userData: User },
  string,
  { rejectValue: string }
>('auth/fetchCurrentUser', async (sessionToken, { rejectWithValue }) => {
  try {
    const { data } = await userApi.post('Login/Me', { sessionToken });
    const decoded: any = jwtDecode(data.token);

    const { role, roles } = normalizeRoles(data.userData?.roles || data.userData?.role || decoded.role);

    const userData: User = {
      user_id: data.userData?.user_Id || Number(decoded.nameid),
      email: data.userData?.email,
      name: data.userData?.name || decoded.email?.split('@')[0],
      role,
      roles,
      permission: data.userData?.permissions || decoded.permission || [],
      imageUrl: data.userData?.imageUrl,
      candidate_id: data.userData?.candidate_id || decoded.candidate_id,
      isManager: data.userData?.isManager || decoded.isManager,
      isTeamLead: data.userData?.isTeamLead || false,
      organizationId: data.userData?.organizationId || data.userData?.OrganizationId || decoded.organizationId || decoded.OrganizationId,
      isDefaultPass: data.userData?.isDefaultPass ?? decoded.isDefaultPass ?? false,
      employee_Id: data.userData?.employee_Id ?? null,
    };

    return { token: data.token, userData };
  } catch {
    return rejectWithValue('Session expired');
  }
});

// registerUser: signs up a new candidate via userService
export const registerUser = createAsyncThunk<any, Record<string, any>, { rejectValue: any }>(
  'auth/registerUser',
  async (userData, { rejectWithValue }) => {
    try {
      const data = await addCandidateSignup(userData as any);
      return data;
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Registration failed';
      return rejectWithValue(msg);
    }
  }
);

// verifyOtp: verifies email OTP via userService
export const verifyOtp = createAsyncThunk<any, { email: string; otp: string }, { rejectValue: string }>(
  'auth/verifyOtp',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const data = await verifyEmail({ email, otp });
      return data;
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'OTP verification failed';
      return rejectWithValue(msg);
    }
  }
);

// requestPasswordReset: sends password reset link via userService
export const requestPasswordReset = createAsyncThunk<any, { email: string }, { rejectValue: string }>(
  'auth/requestPasswordReset',
  async ({ email }, { rejectWithValue }) => {
    try {
      const data = await sendResetLink({ email });
      return data;
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to send reset link';
      return rejectWithValue(msg);
    }
  }
);

// passwordReset: resets password via userService (Swagger: POST /Users/reset-password)
export const passwordReset = createAsyncThunk<
  any,
  { email: string; token: string; newPassword: string },
  { rejectValue: string }
>('auth/passwordReset', async ({ email, token, newPassword }, { rejectWithValue }) => {
  try {
    const data = await resetPasswordService({ email, token, newPassword });
    return data;
  } catch (err: any) {
    const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Password reset failed';
    return rejectWithValue(msg);
  }
});
export const getUserById = createAsyncThunk<User, number, { rejectValue: string }>(
  'auth/getUserById',
  async (userId, { rejectWithValue }) => {
    try {
      const data = await getUserByIdService(userId);
      const user: User = {
        user_id: data.userId,
        name: data.userName,
        email: data.email,
        contactNumber: data.phoneNumber,
        imageUrl: data.imageUrl,
        isEmailVerified: data.isEmailverified,
        role: data.role,
        permission: data.permissions || [],
        isActive: data.isActive,
        isDefaultPass: data.isDefaultPass ?? false,
      };
      return user;
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to fetch user';
      return rejectWithValue(msg);
    }
  }
);
export const getCurrentUserProfile = createAsyncThunk<User, void, { rejectValue: string }>(
  'auth/getCurrentUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getUserProfile();
      const user: User = {
        user_id: data.userId,
        name: data.userName,
        email: data.email,
        contactNumber: data.phoneNumber,
        imageUrl: data.imageUrl,
        isEmailVerified: data.isEmailverified,
        role: data.role,
        permission: data.permissions || [],
        isActive: data.isActive,
        isDefaultPass: data.isDefaultPass ?? false,
      };
      return user;
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to fetch profile';
      return rejectWithValue(msg);
    }
  }
);
export const updateUser = createAsyncThunk<
  any,
  { Id: string; FullName: string; contactNumber: string; ProfileImage?: File },
  { rejectValue: string }
>('auth/updateUser', async ({ Id, FullName, contactNumber, ProfileImage }, { rejectWithValue }) => {
  try {
    const data = await updateProfile({
      id: Number(Id),
      name: FullName,
      contactNumber,
      profileImage: ProfileImage,
    });
    return data;
  } catch (err: any) {
    const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to update profile';
    return rejectWithValue(msg);
  }
});
export const updatePasswordByUser = createAsyncThunk<
  any,
  { userId: number; newPassword: string; confirmPassword: string },
  { rejectValue: string }
>('auth/updatePasswordByUser', async ({ userId, newPassword, confirmPassword }, { rejectWithValue }) => {
  try {
    const data = await updatePasswordService({ userId, newPassword, confirmPassword });
    return data;
  } catch (err: any) {
    const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to update password';
    return rejectWithValue(msg);
  }
});

export const selfUpdatePasswordByUser = createAsyncThunk<
  any,
  { userId: number; newPassword: string; currentPassword: string },
  { rejectValue: string }
>('auth/selfUpdatePasswordByUser', async ({ userId, newPassword, currentPassword }, { rejectWithValue }) => {
  try {
    const data = await selfUpdatePasswordService({ userId, newPassword, currentPassword });
    return data;
  } catch (err: any) {
    const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to update password';
    return rejectWithValue(msg);
  }
});

// deleteUser: deletes a user via userService
export const deleteUser = createAsyncThunk<{ userId: number }, number, { rejectValue: string }>(
  'auth/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      await deleteUserService(userId);
      return { userId };
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to delete user';
      return rejectWithValue(msg);
    }
  }
);


const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.activeRole = null;
      clearAuthStorage();
      toast.success('Logged out successfully');
    },
    setActiveRole: (state, action: PayloadAction<string>) => {
      state.activeRole = action.payload;
      localStorage.setItem('activeRole', action.payload);
    },
    clearError(state) {
      state.error = null;
    },
    resetOtpState(state) {
      state.otpVerified = false;
      state.signupStatus = null;
    },
    clearResetStatus(state) {
      state.passwordResetStatus = null;
      state.passwordResetRequestStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.userData;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        // Deduplicate permissions (backend encodes them twice in JWT — known bug)
        state.permissions = [...new Set(action.payload.userData.permission || [])];
        state.userId = action.payload.userData.user_id ?? null;
        // /Login/login returns userData: null — employee_Id is only available once
        // fetchCurrentUser (/Login/Me) resolves, which Login.tsx always awaits before navigating.
        state.employeeId = (action.payload.userData as any).employee_Id ?? null;
        const storedRole = localStorage.getItem('activeRole');
        const firstRole = Array.isArray(action.payload.userData.role)
          ? action.payload.userData.role[0]
          : action.payload.userData.role;
        // Don't clobber a role already resolved at login (e.g. the platform account,
        // whose /Me returns no tenant role) — fall back to the current activeRole.
        state.activeRole = storedRole || firstRole || state.activeRole || null;
        const userDetails = {
          user_id: action.payload.userData.user_id,
          name: action.payload.userData.name,
          email: action.payload.userData.email,
          contactNumber: action.payload.userData.contactNumber,
          imageUrl: action.payload.userData.imageUrl,
          role: action.payload.userData.role,
          roles: action.payload.userData.roles,
          permission: action.payload.userData.permission,
          organizationId: action.payload.userData.organizationId,
          isEmailVerified: action.payload.userData.isEmailVerified,
          isManager: action.payload.userData.isManager,
          candidate_id: action.payload.userData.candidate_id,
          isDefaultPass: action.payload.userData.isDefaultPass,
        };
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('userDetails', JSON.stringify(userDetails));

      })

      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.activeRole = null;
        state.error = action.payload || 'Authentication failed';
        // Don't show toast here - let the component handle it
      })

      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.userData;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        // Deduplicate permissions (backend encodes them twice in JWT — known bug)
        state.permissions = [...new Set(action.payload.userData.permission || [])];
        state.userId = action.payload.userData.user_id ?? null;
        state.employeeId = (action.payload.userData as any).employee_Id ?? null;
        const storedRole = localStorage.getItem('activeRole');
        const firstRole = Array.isArray(action.payload.userData.role)
          ? action.payload.userData.role[0]
          : action.payload.userData.role;
        // Don't clobber a role already resolved at login (e.g. the platform account,
        // whose /Me returns no tenant role) — fall back to the current activeRole.
        state.activeRole = storedRole || firstRole || state.activeRole || null;
        const userDetails = {
          user_id: action.payload.userData.user_id,
          name: action.payload.userData.name,
          email: action.payload.userData.email,
          contactNumber: action.payload.userData.contactNumber,
          imageUrl: action.payload.userData.imageUrl,
          role: action.payload.userData.role,
          roles: action.payload.userData.roles,
          permission: action.payload.userData.permission,
          organizationId: action.payload.userData.organizationId,
          isEmailVerified: action.payload.userData.isEmailVerified,
          isManager: action.payload.userData.isManager,
          isTeamLead: action.payload.userData.isTeamLead,
          isDefaultPass: action.payload.userData.isDefaultPass,
        };
        localStorage.setItem('userDetails', JSON.stringify(userDetails));
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload;
        // Only clear internal auth keys — preserve client-portal data (token, clientUser)
        // so that client-portal sessions aren't wiped when sessionToken refresh fails.
        const isClientPortal = !!localStorage.getItem('clientUser');
        if (isClientPortal) {
          localStorage.removeItem('sessionToken');
          localStorage.removeItem('userDetails');
          localStorage.removeItem('activeRole');
        } else {
          clearAuthStorage();
        }
      })

      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.signupStatus = null;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.signupStatus = action.payload.message || 'User registered. OTP sent.';
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.signupStatus = null;
        state.error = action.payload;
      })

      .addCase(verifyOtp.fulfilled, (state) => {
        state.otpVerified = true;
        state.loading = false;
      })
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.otpVerified = false;
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(requestPasswordReset.fulfilled, (state, action) => {
        state.passwordResetRequestStatus = action.payload.message;
        state.loading = false;
      })
      .addCase(requestPasswordReset.rejected, (state, action) => {
        state.passwordResetRequestStatus = null;
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(passwordReset.fulfilled, (state, action) => {
        state.passwordResetStatus = action.payload.message;
        state.loading = false;
      })
      .addCase(passwordReset.rejected, (state, action) => {
        state.passwordResetStatus = null;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getCurrentUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;

        // Update localStorage with the current user profile
        const userDetails = {
          user_id: action.payload.user_id,
          name: action.payload.name,
          email: action.payload.email,
          contactNumber: action.payload.contactNumber,
          imageUrl: action.payload.imageUrl,
          role: action.payload.role,
          permission: action.payload.permission,
          isEmailVerified: action.payload.isEmailVerified,
          isActive: action.payload.isActive,
          isDefaultPass: action.payload.isDefaultPass,
        };
        localStorage.setItem('userDetails', JSON.stringify(userDetails));
      })
      .addCase(getCurrentUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.loading = false;
        // You can choose to update the current user if it's the same user, or handle it differently
        const currentUserId = state.user?.user_id;
        const fetchedUserId = action.payload.user_id;

        if (currentUserId && String(currentUserId) === String(fetchedUserId)) {
          state.user = action.payload;
        }
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const updatedUser = action.payload.userData || action.payload;
        const currentUserId = state.user?.user_id || state.user?.userId;
        const updatedUserId = updatedUser.user_id || updatedUser.userId;

        if (currentUserId && updatedUserId && String(currentUserId) === String(updatedUserId)) {
          state.user = updatedUser;

          const newUser = {
            ...JSON.parse(localStorage.getItem('userDetails') || '{}'),
            name: updatedUser.name,
            email: updatedUser.email,
            contactNumber: updatedUser.contactNumber,
            imageUrl: updatedUser.imageUrl,
            role: updatedUser.role?.roleName,
          };

          localStorage.setItem('userDetails', JSON.stringify(newUser));
        }
      })

      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updatePasswordByUser.fulfilled, (state, action) => {
      })
      .addCase(selfUpdatePasswordByUser.fulfilled, (state, action) => {
        if (state.user) {
          state.user.isDefaultPass = false;
        }

        const existingDetailsRaw = localStorage.getItem('userDetails');
        if (existingDetailsRaw) {
          try {
            const userDetails = JSON.parse(existingDetailsRaw);
            userDetails.isDefaultPass = false;
            localStorage.setItem('userDetails', JSON.stringify(userDetails));
          } catch (e) { }
        }
      });
  },
});



export const { logout, setActiveRole, clearError, resetOtpState, clearResetStatus } = authSlice.actions;
export default authSlice.reducer;